import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsPrescription2 } from "react-icons/bs";
import { BarChart } from "@mantine/charts";
import {
  Center,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAccessPoint,
  IconClipboardData,
  IconFirstAidKit,
  IconMoneybag,
} from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import en from "date-fns/locale/en-US";
import fr from "date-fns/locale/fr";
import { useShallow } from "zustand/react/shallow";

import { DateRangeSelect } from "@pages/AdminDashboard/components/DateRangeSelect";
import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import {
  consultationsTrendSeries,
  labsTrendSeries,
  prescriptionsTrendSeries,
} from "@data/series";

import { useGetConsultationsTrend } from "@hooks/queries/dashboard/useGetConsultationsTrend";
import { useGetLabsTrend } from "@hooks/queries/dashboard/useGetLabsTrend";
import { useGetPrescriptionsTrend } from "@hooks/queries/dashboard/useGetPrescriptionsTrend";
import { useGetSingleStaffSession } from "@hooks/queries/dashboard/useGetSingleStaffSession";
import { useGetTotalConsultations } from "@hooks/queries/dashboard/useGetTotalConsultations";
import { useGetTotalEarnings } from "@hooks/queries/dashboard/useGetTotalEarnings";
import { useGetTotalLabStatuses } from "@hooks/queries/dashboard/useGetTotalLabStatuses";
import { useGetTotalPrescriptions } from "@hooks/queries/dashboard/useGetTotalPrescriptions";
import { useTelemetryStore } from "@hooks/store/useTelemetryStore";

import { useAuth } from "@contexts/auth";


export const Overview = () => {
  const { t, i18n } = useTranslation();
  const { id: userId } = useAuth();
  const [signInAttempts] = useTelemetryStore(
    useShallow(state => [state.signInAttempts])
  );

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [trendTarget, setTrendTarget] = useState<number | string>();
  const [trendsFilter, setTrendsFilter] = useState<string | null>(
    "consultations"
  );
  const [trendData, setTrendData] = useState<any>([]);
  const [trendSeries, setTrendSeries] = useState<
    { name: string; color: string }[]
  >(consultationsTrendSeries);

  const {
    data: consultations,
    isLoading: isConsultationsLoading,
    isSuccess: isConsultationsSuccess,
    isError: isConsultationsError,
  } = useGetTotalConsultations({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: labs,
    isLoading: isLabsLoading,
    isSuccess: isLabsSuccess,
    isError: isLabsError,
  } = useGetTotalLabStatuses({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: prescriptions,
    isLoading: isPrescriptionsLoading,
    isSuccess: isPrescriptionsSuccess,
    isError: isPrescriptionsError,
  } = useGetTotalPrescriptions({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: earnings,
    isLoading: isEarningsLoading,
    isSuccess: isEarningsSuccess,
    isError: isEarningsError,
  } = useGetTotalEarnings({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: consultationsTrend,
    isLoading: isConsultationsTrendLoading,
    isSuccess: isConsultationsTrendSuccess,
    isError: isConsultationsTrendError,
  } = useGetConsultationsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: labsTrend,
    isLoading: isLabsTrendLoading,
    isError: isLabsTrendError,
    isSuccess: isLabsTrendSuccess,
  } = useGetLabsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: prescriptionsTrend,
    isLoading: isPrescriptionsTrendLoading,
    isError: isPrescriptionsTrendError,
    isSuccess: isPrescriptionsTrendSuccess,
  } = useGetPrescriptionsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: staffSession,
    isLoading: isStaffSessionLoading,
    isError: isStaffSessionError,
    isSuccess: isStaffSessionSuccess,
  } = useGetSingleStaffSession({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });

  useEffect(() => {
    if (trendsFilter) {
      if (trendsFilter === "consultations") {
        setTrendSeries(consultationsTrendSeries);
        setTrendData(consultationsTrend);
      } else if (trendsFilter === "labs") {
        setTrendData(labsTrend);
        setTrendSeries(labsTrendSeries);
      } else if (trendsFilter === "prescriptions") {
        setTrendData(prescriptionsTrend);
        setTrendSeries(prescriptionsTrendSeries);
      }
    }
  }, [trendsFilter, labsTrend, consultationsTrend]);

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={"staff"}>
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 3, lg1: 4 }} mt={"2rem"}>
          <OverviewCard
            title={t("total-consultations")}
            amount={consultations?.total || 0}
            isLoading={isConsultationsLoading}
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconFirstAidKit
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-labs")}
            amount={labs?.total || 0}
            isLoading={isLabsLoading}
            icon={
              <Group
                bg="blue.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconClipboardData
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-blue-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-prescriptions")}
            amount={prescriptions?.totalPrescriptions || 0}
            isLoading={isPrescriptionsLoading}
            icon={
              <Group
                bg="orange.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <BsPrescription2
                  size={16}
                  color="var(--mantine-color-orange-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-earnings")}
            // amount={earnings?.total || 0}
            isLoading={isEarningsLoading}
            amount={0}
            amountPostfix="CFA"
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconMoneybag
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
            showAlertInfo={t("inaccurate-earnings")}
          />
        </SimpleGrid>

        <Grid mt="3rem" mb={"8rem"} gutter={"lg"}>
          <Grid.Col span={{ base: 12, md: 8, xl: 9 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("trends")}
                </Title>

                <Group gap={"md"}>
                  <Select
                    w={150}
                    data={[
                      { label: t("consultations"), value: "consultations" },
                      { label: t("labs"), value: "labs" },
                      { label: t("prescriptions"), value: "prescriptions" },
                    ]}
                    value={trendsFilter}
                    onChange={setTrendsFilter}
                  />
                  <NumberInput
                    w={150}
                    min={0}
                    value={trendTarget}
                    onChange={setTrendTarget}
                    placeholder={t("type-target")}
                  />
                </Group>
              </Group>

              {renderLoadingErrorData({
                isLoading:
                  isConsultationsTrendLoading ||
                  isLabsTrendLoading ||
                  isPrescriptionsTrendLoading,
                isError:
                  isConsultationsTrendError ||
                  isLabsTrendError ||
                  isPrescriptionsTrendError,
                isSuccess:
                  isConsultationsTrendSuccess ||
                  isLabsTrendSuccess ||
                  isPrescriptionsTrendSuccess,
                dataLength:
                  consultationsTrend?.length ||
                  labsTrend?.length ||
                  prescriptionsTrend?.length ||
                  0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  // <Skeleton h={300} w={"100%"} mt={"1rem"} />
                  <Group h={300} mt={"1rem"} align="end" justify="space-evenly">
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"80%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"20%"} w={"8%"} />
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"70%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"30%"} w={"8%"} />
                    <Skeleton h={"60%"} w={"8%"} />
                  </Group>
                ),
                dataComponent: (
                  <BarChart
                    mt={"1rem"}
                    h={300}
                    data={trendData || []}
                    // yAxisLabel="Consultations"
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    referenceLines={
                      trendTarget
                        ? [
                            {
                              y: trendTarget,
                              color: "red.8",
                              label: t("target"),
                              labelPosition: "insideBottomLeft",
                            },
                          ]
                        : []
                    }
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={trendSeries || []}
                    tickLine="y"
                    tt={"capitalize"}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4, xl: 3 }}>
            <Paper
              withBorder
              p="md"
              h="100%"
              w={{ base: "fit-content", md: "100%" }}
              miw={{ base: 290, md: "auto" }}>
              <Group gap={5}>
                <IconAccessPoint size={20} color="var(--mantine-color-red-5)" />
                <Title order={3} fz={17} fw={500}>
                  {t("session-data")}
                </Title>
              </Group>

              <Stack>
                <Stack gap={"xs"} mt={"1em"}>
                  <Text fz={14} fw={500}>
                    {t("last-login")}:
                  </Text>
                  <Text fz={14} c="dimmed">
                    {signInAttempts?.length
                      ? formatDistanceToNow(
                          new Date(
                            signInAttempts[signInAttempts.length - 1]
                              .timestamp as string
                          ),
                          {
                            // @ts-expect-error - Says type is wrong but looks like it works fine for now
                            locale:
                              i18n.language.slice(0, 2) === "en" ? en : fr,
                            addSuffix: true,
                          }
                        )
                      : "N/A"}
                  </Text>
                </Stack>
                <Stack gap={"xs"} mt={"1em"}>
                  <Text fz={14} fw={500}>
                    {t("total-sessions")}:
                  </Text>
                  <Text fz={14} c="dimmed">
                    {staffSession?.totalSessions || 0}
                  </Text>
                </Stack>
                <Stack gap={"xs"} mt={"1em"}>
                  <Text fz={14} fw={500}>
                    {t("avg-session-duration")}:
                  </Text>
                  <Text fz={14} c="dimmed">
                    {staffSession?.avgSessionDuration
                      ? `${Math.floor(
                          staffSession?.avgSessionDuration / 60
                        )}h ${Math.floor(
                          staffSession?.avgSessionDuration % 60
                        )}m`
                      : "N/A"}
                  </Text>
                </Stack>
                <Stack gap={"xs"} mt={"1em"}>
                  <Text fz={14} fw={500}>
                    {t("total-time-spent")}:
                  </Text>
                  <Text fz={14} c="dimmed">
                    {staffSession?.totalTimeSpent
                      ? `${Math.floor(
                          staffSession?.totalTimeSpent / 60
                        )}h ${Math.floor(staffSession?.totalTimeSpent % 60)}m`
                      : "N/A"}
                  </Text>
                </Stack>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
